/**
 * Spatial boundary rules: objects overlapping the room edge or a fixed
 * exclusion zone, and (lightweight, V1-level) aisle obstruction checks.
 * PRD §8.2 rows: "Room boundary violation", "Aisle obstruction".
 */

import type { LayoutObject, ObjectCatalogEntry, RoomDefinition, ValidationIssue } from '../../types';
import { boundingBox, boxesOverlap } from './geometry';

export function checkRoomBoundary(
  objects: LayoutObject[],
  room: RoomDefinition,
  catalog: ObjectCatalogEntry[]
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const roomBox = { x: 0, y: 0, width: room.widthFt, height: room.lengthFt };

  for (const obj of objects) {
    const box = boundingBox(obj, catalog);
    const outsideRoom =
      box.x < roomBox.x ||
      box.y < roomBox.y ||
      box.x + box.width > roomBox.x + roomBox.width ||
      box.y + box.height > roomBox.y + roomBox.height;

    if (outsideRoom) {
      // Direct match to the brief's "Tables outside room boundaries"
      // Critical example — a layout that doesn't physically fit in the
      // real room is a real, urgent problem, not a matter of degree.
      issues.push({
        id: `boundary-${obj.id}`,
        severity: 'critical',
        message: `${obj.label} overlaps the room boundary.`,
        relatedObjectIds: [obj.id],
      });
      continue;
    }

    // Text notes are deliberately exempt from the fixed-feature-overlap
    // check below — a note like "Do not block doors" is MEANT to sit
    // near or over a door/landmark exclusion zone, since that's the
    // whole point of an annotation. Flagging that placement as a
    // problem would be exactly backwards. The room-boundary check above
    // still applies to notes (a note dragged off the visible room is
    // still worth catching).
    if (obj.type === 'text_note') continue;

    for (const zone of room.exclusionZones) {
      if (boxesOverlap(box, zone)) {
        // A zone whose label identifies it as a door (the Conservatory's
        // "Double Door" / "Sliding Door to Awning Walkway" landmarks —
        // see domain/rooms.ts) maps directly to the brief's "Emergency
        // exits or doors blocked" Critical example. Any other fixed
        // feature (a column, a built-in bar) is still worth flagging,
        // but blocking a generic obstacle isn't the same safety concern
        // as blocking the way out of the room.
        const isDoor = zone.label.toLowerCase().includes('door');
        issues.push({
          id: `exclusion-${obj.id}-${zone.label}`,
          severity: isDoor ? 'critical' : 'warning',
          message: isDoor
            ? `${obj.label} is blocking ${zone.label.toLowerCase()}.`
            : `${obj.label} overlaps a fixed feature (${zone.label}).`,
          relatedObjectIds: [obj.id],
        });
      }
    }
  }

  return issues;
}
